import { useCallback, useEffect, useState } from "react";

import { getModifierSets } from "services/modifierSets/rest";
import { ModifierSetDto } from "services/modifierSets/restModels";

export interface UseModifierSetsResult {
    modifierSets: ModifierSetDto[];
    loading: boolean;
    error: boolean;
    setModifierSets: (modifierSets: ModifierSetDto[]) => void;
    refetch: () => void;
}

/*
* Каталог наборов — справочник филиала: его читают список наборов, редактор позиции и
* страница просмотра меню. Кешируем на уровне модуля (как в useConstants), иначе каждый
* переход «меню → позиция → назад» тянул бы полный каталог заново. Общий промис не даёт
* параллельным монтированиям плодить запросы; изменения из фичи пишутся в кеш через
* setModifierSets, так что следующий экран видит их без запроса.
* */
let cache: ModifierSetDto[] | null = null;
let inFlight: Promise<ModifierSetDto[] | null> | null = null;

const loadModifierSets = (): Promise<ModifierSetDto[] | null> => {
    if (cache) return Promise.resolve(cache);

    if (!inFlight) {
        inFlight = getModifierSets()
            .then((result) => {
                if (result) cache = result;
                return result;
            })
            .finally(() => {
                inFlight = null;
            });
    }

    return inFlight;
};

export const useModifierSets = (): UseModifierSetsResult => {
    const [modifierSets, setLocalModifierSets] = useState<ModifierSetDto[]>(cache || []);
    const [loading, setLoading] = useState<boolean>(!cache);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    const setModifierSets = useCallback((next: ModifierSetDto[]) => {
        cache = next;
        setLocalModifierSets(next);
    }, []);

    const refetch = useCallback(() => {
        cache = null;
        setReloadKey((key) => key + 1);
    }, []);

    useEffect(() => {
        // ignore-флаг: ответ после размонтирования не трогает стейт.
        let ignore = false;

        setLoading(true);
        setError(false);

        loadModifierSets()
            .then((result) => {
                if (ignore) return;
                if (result) {
                    setLocalModifierSets(result);
                } else {
                    setError(true);
                    setLocalModifierSets([]);
                }
            })
            .catch(() => {
                if (ignore) return;
                setError(true);
                setLocalModifierSets([]);
            })
            .finally(() => {
                if (ignore) return;
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [reloadKey]);

    return { modifierSets, loading, error, setModifierSets, refetch };
};
